import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Spinner from '../../components/Spinner';
import {
  fosApi, fosError, formatDate, KIND_LABEL, ROLE_LABEL, STATUS_LABEL, type DirectoryQuery, type DirectoryRow, type DonorKind, type DonorStatus, type HealthQueueItem,
} from '../../lib/fosApi';
import DonorFormDrawer from './DonorFormDrawer';
import { useFos } from './FosShell';
import { Avatar, EmptyState, ErrorNote, HealthChips, Icon, KindChip, Section, StatusChip, useDebounced } from './ui';

const PAGE = 25;

function HealthReview({ canReview }: { canReview: boolean }) {
  const [state, setState] = useState<{ items: HealthQueueItem[]; checked: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  if (!canReview) return null;
  const run = async () => {
    setBusy(true); setError('');
    try { setState(await fosApi.health({})); } catch (cause) { setError(fosError(cause).message); } finally { setBusy(false); }
  };
  return (
    <Section title="Assignment health" icon="alert" className="fos-health"
      action={<button className="btn small" onClick={run} disabled={busy}>{busy ? <Spinner /> : state ? 'Check again' : 'Check assignments'}</button>}>
      {!state && !error && <p className="fos-hint">Find assignments that may no longer work — lapsed registration, an ended Position, no Compass login, or stale import information. Nothing is changed automatically.</p>}
      <ErrorNote message={error} />
      {state && state.items.length === 0 && <p className="fos-ok"><Icon name="check" size={14} /> All {state.checked} active assignment{state.checked === 1 ? '' : 's'} look healthy.</p>}
      {state && state.items.length > 0 && (
        <ul className="fos-health-list">
          {state.items.map((i) => (
            <li key={i.assignmentId}>
              <Avatar name={i.assignee.name} size={30} />
              <div className="fos-health-main">
                <div><Link to={`/fos/donors/${i.donorId}`}>{i.donorName}</Link> <span className="subtitle">· {i.scopeLabel} · {ROLE_LABEL[i.role]}</span></div>
                <div className="subtitle">{i.assignee.name}{i.assignee.positions.length ? ` · ${i.assignee.positions.join(', ')}` : ''}</div>
                <div className="fos-chips"><HealthChips flags={i.flags} /></div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

function Row({ row, onOpen }: { row: DirectoryRow; onOpen: () => void }) {
  const last = row.relationships.map((r) => r.lastInteractionOn ?? '').sort().pop() ?? '';
  return (
    <tr className="fos-row" onClick={onOpen} tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter') onOpen(); }}>
      <td>
        <div className="fos-donor-cell">
          <Avatar name={row.displayName} />
          <div>
            <Link to={`/fos/donors/${row.donorId}`} onClick={(e) => e.stopPropagation()} className="fos-donor-name">{row.displayName}</Link>
            <div className="fos-chips"><span className="fos-ref">{row.ref}</span><KindChip kind={row.kind} />{row.status !== 'active' && <StatusChip status={row.status} />}
              {row.personLinked && <span className="fos-chip link" title="Linked to a Compass person"><Icon name="link" size={12} />Person</span>}
              {row.possibleDuplicate && <span className="fos-chip attention"><Icon name="alert" size={12} />Possible duplicate</span>}</div>
          </div>
        </div>
      </td>
      <td>
        <div className="fos-chips">
          {row.relationships.map((r) => (
            <span key={r.scopeKey} className={`fos-chip scope${r.status === 'inactive' ? ' off' : ''}`} title={r.coordinatorNames.length ? `Primary coordinator: ${r.coordinatorNames.join(', ')}` : 'No primary coordinator assigned'}>
              {r.scopeLabel}{r.coordinatorNames.length ? ` · ${r.coordinatorNames[0]}` : <em> · no coordinator</em>}
            </span>
          ))}
        </div>
      </td>
      <td className="fos-activity-cell">{last ? formatDate(last) : <span className="subtitle">—</span>}</td>
      <td>{row.attention ? <span className="fos-chip attention" title="Assignments needing review"><Icon name="alert" size={12} />{row.attention}</span> : null}</td>
    </tr>
  );
}

export default function FosDonors() {
  const { context } = useFos();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState('');
  const dq = useDebounced(q);
  const [status, setStatus] = useState<DonorStatus | ''>('');
  const [kind, setKind] = useState<DonorKind | ''>('');
  const [scope, setScope] = useState('');
  const [assignment, setAssignment] = useState<'mine' | 'unassigned' | ''>('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [review, setReview] = useState<'duplicates' | ''>('');
  const [sort, setSort] = useState<'name' | 'activity'>('name');
  const [rows, setRows] = useState<DirectoryRow[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [matched, setMatched] = useState(0);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [more, setMore] = useState(false);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(params.get('new') === '1');
  const generation = useRef(0);
  const [reloadTick, setReloadTick] = useState(0);

  const councilWide = context.scopes.viewDonors.council;
  const scopeOptions = useMemo(() => {
    const ids = councilWide ? context.districts : context.districts.filter((d) => context.scopes.viewDonors.districtIds.includes(d.id));
    return [...(councilWide ? [{ key: 'council', label: 'Council relationships' }] : []), ...ids.map((d) => ({ key: `district:${d.id}`, label: d.name }))];
  }, [context, councilWide]);

  const query = useCallback((cursorArg: string | null): DirectoryQuery => ({
    q: dq || undefined, status, kind, scope: scope || undefined, assignment, includeInactive, review, sort, limit: PAGE, cursor: cursorArg,
  }), [dq, status, kind, scope, assignment, includeInactive, review, sort]);

  useEffect(() => {
    const mine = ++generation.current;
    setLoading(true); setError('');
    void fosApi.list(query(null)).then((page) => {
      if (mine !== generation.current) return;
      setRows(page.rows); setCursor(page.nextCursor); setMatched(page.matched); setTruncated(page.truncated);
    }).catch((cause) => { if (mine === generation.current) setError(fosError(cause).message); })
      .finally(() => { if (mine === generation.current) setLoading(false); });
  }, [query, reloadTick]);

  const loadMore = async () => {
    if (!cursor) return;
    const mine = generation.current;
    setMore(true);
    try {
      const page = await fosApi.list(query(cursor));
      if (mine !== generation.current) return;
      setRows((r) => [...r, ...page.rows]); setCursor(page.nextCursor);
    } catch (cause) { setError(fosError(cause).message); } finally { setMore(false); }
  };
  const reload = () => setReloadTick((n) => n + 1);
  const filtersActive = !!(dq || status || kind || scope || assignment || review || includeInactive);
  const clear = () => { setQ(''); setStatus(''); setKind(''); setScope(''); setAssignment(''); setReview(''); setIncludeInactive(false); };
  const canReviewHealth = context.scopes.manageAssignments.council || context.scopes.manageAssignments.districtIds.length > 0;

  return (
    <>
      <div className="fos-toolbar">
        <div className="fos-search">
          <Icon name="search" size={16} />
          <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or DON-number" aria-label="Search donors" />
        </div>
        <select value={scope} onChange={(e) => setScope(e.target.value)} aria-label="Relationship scope">
          <option value="">All my scopes</option>
          {scopeOptions.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <select value={kind} onChange={(e) => setKind(e.target.value as DonorKind | '')} aria-label="Donor type">
          <option value="">Any type</option>
          {(Object.keys(KIND_LABEL) as DonorKind[]).map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value as DonorStatus | '')} aria-label="Donor status">
          <option value="">Any status</option>
          {(Object.keys(STATUS_LABEL) as DonorStatus[]).map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
        </select>
        <select value={assignment} onChange={(e) => setAssignment(e.target.value as 'mine' | 'unassigned' | '')} aria-label="Assignment">
          <option value="">Any assignment</option>
          {context.personId && <option value="mine">Assigned to me</option>}
          <option value="unassigned">No coordinator</option>
        </select>
        <select value={sort} onChange={(e) => setSort(e.target.value as 'name' | 'activity')} aria-label="Sort">
          <option value="name">Sort: name</option>
          <option value="activity">Sort: recent activity</option>
        </select>
        {context.canCreateDonors && <button className="btn primary" onClick={() => setCreating(true)}><Icon name="plus" size={14} /> Add donor</button>}
      </div>
      <div className="fos-toolbar-sub">
        <label className="fos-check"><input type="checkbox" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} /> Include inactive relationships</label>
        {filtersActive && <button className="linkbtn" onClick={clear}>Clear filters</button>}
        <span className="subtitle fos-count">{loading ? '' : `${matched} donor${matched === 1 ? '' : 's'}`}</span>
      </div>

      <HealthReview canReview={canReviewHealth} />
      <ErrorNote message={error} />
      {truncated && <p className="fos-hint">Showing part of a very large directory. Narrow the search to see the rest.</p>}

      <div className="fos-card fos-table-card">
        {loading ? <div className="fos-loading"><Spinner label="Loading donors" /></div>
          : rows.length === 0 ? (
            <EmptyState icon="users" title={filtersActive ? 'No donors match' : 'No donors yet'}>
              {filtersActive ? 'Try fewer filters or a different spelling.' : context.canCreateDonors ? 'Add your first donor to start tracking relationships.' : 'Donors you are allowed to see will appear here.'}
            </EmptyState>
          ) : (
            <div className="fos-table-wrap">
              <table className="fos-table">
                <thead><tr><th>Donor</th><th>Relationships</th><th>Last activity</th><th /></tr></thead>
                <tbody>{rows.map((r) => <Row key={r.donorId} row={r} onOpen={() => navigate(`/fos/donors/${r.donorId}`)} />)}</tbody>
              </table>
            </div>
          )}
        {!loading && rows.length > 0 && (
          <div className="fos-pager">
            <span className="subtitle">Showing {rows.length} of {matched}</span>
            {cursor && <button className="btn" onClick={loadMore} disabled={more}>{more ? <Spinner /> : 'Load more'}</button>}
          </div>
        )}
      </div>

      {creating && <DonorFormDrawer context={context} onClose={() => { setCreating(false); if (params.get('new')) { params.delete('new'); setParams(params, { replace: true }); } }} onSaved={() => { setCreating(false); reload(); }} />}
    </>
  );
}
